import { forwardRef, type CSSProperties, type ReactNode } from 'react';
import { cn } from '../../lib/cn';
import { sanitizeInlineHtml } from '../../lib/sanitize-inline-html';
import { SIcon } from '../SIcon';

export type SCalloutType = 'default' | 'danger';

/** 중첩 메시지: 문자열 또는 (한 단계 더 들어간) 문자열 배열 */
export type SCalloutMessage = string | SCalloutMessage[];

export interface SCalloutProps {
  /** 콜아웃 타입 (색상·아이콘·타이틀 결정) */
  type?: SCalloutType;
  /**
   * 표시할 메시지 목록 (배열 중첩으로 depth 표현).
   * 각 문자열은 인라인 HTML(`<b>`, `<a>` 등)을 허용하며 sanitizeInlineHtml 로 살균 후 렌더된다.
   */
  message?: SCalloutMessage[];
  /** message 대신 직접 본문을 넣을 때 */
  children?: ReactNode;
  className?: string;
  style?: CSSProperties;
}

const DANGER_TITLE_LABEL = '주의사항';

function renderItems(message: SCalloutMessage[], depth = 1): ReactNode[] {
  const out: ReactNode[] = [];
  message.forEach((m, i) => {
    if (Array.isArray(m)) {
      out.push(...renderItems(m, depth + 1));
      return;
    }
    const isDepth2 = depth >= 2;
    out.push(
      <li
        key={`${depth}-${i}-${out.length}`}
        className={cn('flex items-start', isDepth2 && 'pl-[32px]')}
      >
        <span className="w-[24px] shrink-0 text-center" aria-hidden>
          {isDepth2 ? '•' : '-'}
        </span>
        {/* 메시지는 HTML 문자열을 허용하되, sanitizeInlineHtml 로 살균 후 innerHTML 렌더 (XSS 방어) */}
        <p
          className="m-0 min-w-0 flex-1 break-words"
          dangerouslySetInnerHTML={{ __html: sanitizeInlineHtml(m) }}
        />
      </li>,
    );
  });
  return out;
}

/**
 * SCallout — sd-callout 포팅.
 * default/danger 타입, 중첩 리스트(불릿은 span으로 렌더), danger는 좌측 타이틀 표시.
 * 색상은 `--cmp-callout-*` 토큰 참조.
 * message 각 항목은 sanitizeInlineHtml 로 살균한 뒤 innerHTML 로 렌더한다 (원본 sd-callout 과 동일).
 */
export const SCallout = forwardRef<HTMLDivElement, SCalloutProps>(function SCallout(
  { type = 'default', message = [], children, className, style },
  ref,
) {
  const isDanger = type === 'danger';
  const vars = {
    '--co-bg': `var(--cmp-callout-${type}-bg)`,
    '--co-border': `var(--cmp-callout-${type}-border)`,
    '--co-content': `var(--cmp-callout-${type}-content)`,
  } as CSSProperties;

  return (
    <div
      ref={ref}
      role="note"
      className={cn(
        'inline-flex w-full items-stretch overflow-hidden rounded-[var(--cmp-callout-radius)] border-[length:var(--cmp-callout-border-width)] border-solid',
        'border-[color:var(--co-border)] bg-[var(--co-bg)] text-[color:var(--co-content)]',
        className,
      )}
      style={{ ...vars, ...style }}
    >
      {isDanger && (
        <div
          className="flex shrink-0 flex-col items-center justify-center gap-[var(--cmp-callout-danger-title-gap)] px-[var(--cmp-callout-danger-title-paddingX)] py-[8px] text-[16px] font-bold leading-[26px]"
          style={{
            background: 'var(--cmp-callout-danger-title-bg)',
            color: 'var(--cmp-callout-danger-title-color)',
          }}
        >
          <SIcon name="warningFill" size={24} color="var(--cmp-callout-danger-title-color)" />
          <span className="whitespace-nowrap">{DANGER_TITLE_LABEL}</span>
        </div>
      )}
      <div className="flex min-w-0 flex-1 flex-col justify-center gap-[var(--cmp-callout-body-gap)] px-[var(--cmp-callout-body-paddingX)] py-[var(--cmp-callout-body-paddingY)] text-[12px] font-normal leading-[20px]">
        {children ?? (
          <ul className="m-0 flex list-none flex-col justify-center gap-[var(--cmp-callout-body-gap)] p-0">
            {renderItems(message)}
          </ul>
        )}
      </div>
    </div>
  );
});
